import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useTheme } from '../context/ThemeContext';
import '../styles/landing.css';
import { WhatsAppFab, openWhatsApp, WA_MESSAGES } from '../components/WhatsApp';

/**
 * Where "Open the demo" goes.
 *
 * ⚠ THE SUBDOMAIN, not /?tenant=demo.
 *
 * The query-string form works — TenantResolutionMiddleware reads it — but it leaves the visitor on
 * the ROOT domain. Everything that follows is then scoped to eduvoratech.com: the PWA installs
 * against the marketing site, the manifest is the generic Eduvora one, and the address bar shows a
 * URL no school would recognise as theirs.
 *
 * ⚠ Kept as a computed value rather than a hardcoded string so it still works on a preview
 * deployment or localhost, where "demo.eduvoratech.com" does not exist. On any host that isn't
 * a plain apex domain we fall back to the query-string form.
 */
const DEMO_URL = (() => {
  if (typeof window === 'undefined') return '/?tenant=demo';
  const { protocol, host } = window.location;
  // Only rewrite for the real apex domain; localhost and preview hosts keep the query form.
  return host === 'eduvoratech.com' || host === 'www.eduvoratech.com'
    ? `${protocol}//demo.eduvoratech.com/`
    : '/?tenant=demo';
})();

/* ============================================================================
   EDUVORA LANDING — "The Orbit"
   A living universe: module nodes orbit one glowing core among depth-projected
   stars. Custom canvas engine — zero dependencies, DPR-aware, pauses when the
   tab is hidden, renders a single static frame under prefers-reduced-motion.
   Night = deep space (study night). Day = dawn paper with gold dust.
   ========================================================================== */

/* ----------------------------- orbit engine ------------------------------ */

interface Star { x: number; y: number; z: number; tw: number; sp: number; }
interface Ring { tilt: number; rx: number; rz: number; speed: number; phase: number; }

/**
 * Four rings, one per module family.
 *
 * ⚠ A FOURTH was ADDED here. The NODES list below now references `ring: 3` — with only three rings,
 * RINGS[3] would be `undefined` and the canvas would crash on the first frame. The counts must match.
 *
 * Each ring is slightly wider and slower than the last, and tilted the other way, so the system reads
 * as depth rather than as concentric circles.
 */
const RINGS: Ring[] = [
  { tilt: 0.92, rx: 0.60, rz: 0.36, speed: 0.100, phase: 0.0 },
  { tilt: -0.55, rx: 0.82, rz: 0.50, speed: -0.072, phase: 1.1 },
  { tilt: 0.28, rx: 1.04, rz: 0.64, speed: 0.048, phase: 2.3 },
  { tilt: -0.72, rx: 1.24, rz: 0.76, speed: -0.034, phase: 3.4 },
];

/**
 * The orbit. Twelve nodes, four rings — one per module family.
 *
 * Deliberately NOT all eighteen. The canvas is a picture, not an inventory: past about a dozen
 * labels it stops reading as a system and starts reading as clutter. The full list lives in the
 * cards below, where a reader has actually asked for detail.
 */
const NODES: { label: string; ring: number; offset: number }[] = [
  { label: 'ADMISSIONS', ring: 0, offset: 0.00 },
  { label: 'FEES', ring: 0, offset: 0.33 },
  { label: 'ATTENDANCE', ring: 0, offset: 0.66 },

  { label: 'EXAMS', ring: 1, offset: 0.00 },
  { label: 'TIMETABLE', ring: 1, offset: 0.33 },
  { label: 'CLASSROOM', ring: 1, offset: 0.66 },

  { label: 'TRANSPORT', ring: 2, offset: 0.00 },
  { label: 'HOSTEL', ring: 2, offset: 0.33 },
  { label: 'LIBRARY', ring: 2, offset: 0.66 },

  { label: 'HR · PAYROLL', ring: 3, offset: 0.00 },
  { label: 'THE GATE', ring: 3, offset: 0.33 },
  { label: 'FAMILY', ring: 3, offset: 0.66 },
];

interface Palette {
  star: string; starDim: string; ring: string; link: string;
  coreA: string; coreB: string; node: string; label: string; comet: string;
}

const NIGHT: Palette = {
  star: '#E8D9B8', starDim: '#8FA3BE', ring: 'rgba(201,161,90,0.16)',
  link: 'rgba(201,161,90,0.10)', coreA: 'rgba(233,205,150,0.85)',
  coreB: 'rgba(201,161,90,0)', node: '#E9CD96', label: 'rgba(224,230,240,0.72)',
  comet: 'rgba(210,225,255,0.55)',
};

// ⚠ Light mode was far too faint. Rings at 0.20 alpha and links at 0.12 on a near-white background
// are effectively invisible — which is why the hero read as a blank page with a smudge behind the
// headline. These are lifted enough to be structure you can actually see, while staying well below
// the contrast of the copy so they never compete with it.
const DAWN: Palette = {
  star: '#A07A32', starDim: '#BFA875', ring: 'rgba(138,106,46,0.38)',
  link: 'rgba(138,106,46,0.22)', coreA: 'rgba(176,138,66,0.42)',
  coreB: 'rgba(176,138,66,0)', node: '#7A5C24', label: 'rgba(40,46,58,0.80)',
  comet: 'rgba(120,100,60,0.55)',
};

function OrbitCanvas({ mode }: { mode: 'light' | 'dark' }) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const modeRef = useRef(mode);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ---- state ----
    let w = 0, h = 0, dpr = 1, raf = 0, running = true;
    let mx = 0, my = 0;            // pointer target (-1..1)
    let rx = 0, ry = 0;            // eased camera angles
    let zoom = 1;

    const stars: Star[] = [];
    // More stars, spread wider — the field was tuned for a small centred orbit and looked sparse
    // (and left the outer canvas empty) once the system was scaled up to span the viewport.
    const starCount = 460;
    for (let i = 0; i < starCount; i++) {
      // shell distribution so the middle stays clear for the orbit
      const r = 0.55 + Math.random() * 1.45;
      const a = Math.random() * Math.PI * 2;
      const b = Math.acos(2 * Math.random() - 1);
      stars.push({
        x: r * Math.sin(b) * Math.cos(a),
        y: r * Math.cos(b) * 0.72,
        z: r * Math.sin(b) * Math.sin(a),
        tw: Math.random() * Math.PI * 2,
        sp: 0.4 + Math.random() * 1.2,
      });
    }

    // one slow comet
    let cometT = -0.2;

    const resize = () => {
      const rect = wrap.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.max(1, Math.floor(rect.width));
      h = Math.max(1, Math.floor(rect.height));
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    resize();

    const clamp = (v: number) => Math.max(-1, Math.min(1, v));
    const onPointer = (e: PointerEvent) => {
      const r = wrap.getBoundingClientRect();
      mx = clamp(((e.clientX - r.left) / r.width) * 2 - 1);
      my = clamp(((e.clientY - r.top) / r.height) * 2 - 1);
    };
    const onScroll = () => {
      zoom = 1 + Math.min(window.scrollY, 620) / 620 * 0.10;
    };
    const onVis = () => {
      running = !document.hidden;
      if (running && !reduced) {
        cancelAnimationFrame(raf);          // never double-schedule the loop
        raf = requestAnimationFrame(frame);
      }
    };

    if (!reduced) {
      window.addEventListener('pointermove', onPointer, { passive: true });
      window.addEventListener('scroll', onScroll, { passive: true });
      document.addEventListener('visibilitychange', onVis);
    }

    // rotate a 3D point by camera angles, project to screen
    const project = (x: number, y: number, z: number, R: number) => {
      // rotY
      const cy = Math.cos(ry), sy = Math.sin(ry);
      let px = x * cy - z * sy;
      let pz = x * sy + z * cy;
      // rotX
      const cx = Math.cos(rx), sx = Math.sin(rx);
      const py = y * cx - pz * sx;
      pz = y * sx + pz * cx;
      const f = 3.4;
      const s = f / (f - pz);
      // ⚠ The centre used to be hardcoded to w/2 — dead behind the headline, so the copy sat on top
      // of the busiest part of the drawing and both suffered. On a wide screen the system now sits
      // to the RIGHT of the text column; on narrow screens it returns to centre, where the hero
      // stacks vertically and there is no text to collide with.
      const wide = w > 900;
      const originX = wide ? w * 0.64 : w / 2;
      const originY = wide ? h * 0.52 : h * 0.46;
      return { sx: originX + px * s * R, sy: originY + py * s * R * 0.92, s, depth: pz };
    };

    const ringPoint = (ring: Ring, u: number) => {
      const a = u * Math.PI * 2;
      let x = Math.cos(a) * ring.rx;
      let z = Math.sin(a) * ring.rz;
      let y = 0;
      // tilt about X
      const ct = Math.cos(ring.tilt), st = Math.sin(ring.tilt);
      const y2 = y * ct - z * st;
      const z2 = y * st + z * ct;
      // slight lean about Z for asymmetry
      const lean = 0.18;
      const cl = Math.cos(lean), sl = Math.sin(lean);
      const x2 = x * cl - y2 * sl;
      const y3 = x * sl + y2 * cl;
      return { x: x2, y: y3, z: z2 };
    };

    const frame = () => {
      if (!running) return;
      const p = modeRef.current === 'dark' ? NIGHT : DAWN;
      const t = performance.now() / 1000;

      // ease camera toward pointer
      ry += ((mx * 0.22 + t * 0.02) - ry) * 0.04;
      rx += ((my * 0.14 - 0.06) - rx) * 0.05;

      // ⚠ Sizing used to be Math.min(w, h) — on a wide desktop that's the HEIGHT, so the whole system
      // stayed small and clustered in the middle, colliding with the headline while leaving the rest
      // of the canvas empty. The page read as blank with a smudge in the centre.
      //
      // Bias toward WIDTH so the orbit actually spans the viewport, with a height guard so it doesn't
      // overflow on short/landscape windows.
      const R = Math.min(w * 0.30, h * 0.62) * zoom;

      ctx.clearRect(0, 0, w, h);

      // ---- stars ----
      for (const s of stars) {
        const pr = project(s.x, s.y, s.z, R * 1.9);
        if (pr.depth > 1.4) continue;
        const tw = 0.55 + 0.45 * Math.sin(t * s.sp + s.tw);
        const size = Math.max(0.4, 1.5 * pr.s * tw);
        const near = pr.s > 1.02;
        ctx.globalAlpha = (near ? 0.85 : 0.45) * tw;
        ctx.fillStyle = near ? p.star : p.starDim;
        ctx.beginPath();
        ctx.arc(pr.sx, pr.sy, size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // ---- comet ----
      cometT += 0.0012;
      if (cometT > 1.25) cometT = -0.25;
      const cx0 = w * (cometT), cy0 = h * (0.18 + 0.08 * Math.sin(cometT * 5));
      const grad = ctx.createLinearGradient(cx0 - 90, cy0 + 24, cx0, cy0);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, p.comet);
      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(cx0 - 90, cy0 + 24);
      ctx.lineTo(cx0, cy0);
      ctx.stroke();

      // ---- rings ----
      ctx.lineWidth = 1;
      for (const ring of RINGS) {
        ctx.strokeStyle = p.ring;
        ctx.beginPath();
        const steps = 96;
        for (let i = 0; i <= steps; i++) {
          const pt = ringPoint(ring, i / steps);
          const pr = project(pt.x, pt.y, pt.z, R);
          if (i === 0) ctx.moveTo(pr.sx, pr.sy);
          else ctx.lineTo(pr.sx, pr.sy);
        }
        ctx.stroke();
      }

      // ---- core ----
      // The glow used to spread to 3.2x its radius, producing a large soft bloom that sat directly
      // behind the headline and washed the letters out. Tighter falloff now: it reads as a bright
      // core rather than a haze over the copy.
      const core = project(0, 0, 0, R);
      const pulse = 1 + 0.06 * Math.sin(t * 1.4);
      const cr = Math.min(w, h) * 0.048 * pulse;
      const g = ctx.createRadialGradient(core.sx, core.sy, 0, core.sx, core.sy, cr * 2.2);
      g.addColorStop(0, p.coreA);
      g.addColorStop(1, p.coreB);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(core.sx, core.sy, cr * 2.2, 0, Math.PI * 2);
      ctx.fill();

      // ---- nodes + links + labels ----
      ctx.font = '600 10px "IBM Plex Mono", ui-monospace, monospace';
      ctx.textAlign = 'center';
      for (const n of NODES) {
        const ring = RINGS[n.ring];
        const u = (t * ring.speed + ring.phase / (Math.PI * 2) + n.offset) % 1;
        const pt = ringPoint(ring, u < 0 ? u + 1 : u);
        const pr = project(pt.x, pt.y, pt.z, R);
        const behind = pr.depth > 0.25;

        // link to core
        ctx.strokeStyle = p.link;
        ctx.globalAlpha = behind ? 0.4 : 1;
        ctx.beginPath();
        ctx.moveTo(core.sx, core.sy);
        ctx.lineTo(pr.sx, pr.sy);
        ctx.stroke();

        // node
        const nr = Math.max(1.6, 3.1 * pr.s);
        const ng = ctx.createRadialGradient(pr.sx, pr.sy, 0, pr.sx, pr.sy, nr * 3);
        ng.addColorStop(0, p.coreA);
        ng.addColorStop(1, p.coreB);
        ctx.fillStyle = ng;
        ctx.beginPath();
        ctx.arc(pr.sx, pr.sy, nr * 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = p.node;
        ctx.beginPath();
        ctx.arc(pr.sx, pr.sy, nr, 0, Math.PI * 2);
        ctx.fill();

        // Label. It used to render at a flat, very low opacity — legible enough to muddy anything
        // behind it, too faint to actually read, so it looked like dirt on the canvas. Now it fades
        // with depth (near nodes read clearly, far ones recede) and sits a little further out.
        if (!behind && w > 560) {
          ctx.save();
          ctx.globalAlpha = Math.max(0.35, Math.min(1, (pr.s - 0.8) * 2.2));
          ctx.fillStyle = p.label;
          ctx.font = `600 ${Math.max(9, Math.round(10.5 * pr.s))}px "IBM Plex Mono", ui-monospace, monospace`;
          ctx.fillText(n.label, pr.sx, pr.sy - nr - 9);
          ctx.restore();
        }
        ctx.globalAlpha = 1;
      }

      if (!reduced) raf = requestAnimationFrame(frame);
    };

    if (reduced) {
      // one static, composed frame — no loop, no parallax
      ry = 0.4;
      rx = -0.12;
      frame();
    } else {
      raf = requestAnimationFrame(frame);
    }

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      if (!reduced) {
        window.removeEventListener('pointermove', onPointer);
        window.removeEventListener('scroll', onScroll);
        document.removeEventListener('visibilitychange', onVis);
      }
    };
  }, []);

  return (
    <div ref={wrapRef} className="lp-orbit" aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
}

/* ----------------------------- reveal + count ---------------------------- */

function useReveal() {
  useEffect(() => {
    const els = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (!('IntersectionObserver' in window)) { els.forEach((e) => e.classList.add('lp-in')); return; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('lp-in'); io.unobserve(en.target); } }),
      { threshold: 0.15 },
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, []);
}

function Counter({ to, suffix }: { to: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { el.textContent = `${to}${suffix ?? ''}`; return; }
    let raf = 0;
    const io = new IntersectionObserver((ents) => {
      if (!ents[0].isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const dur = 1200;
      const tick = (now: number) => {
        const k = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - k, 3);
        el.textContent = `${Math.round(to * eased)}${suffix ?? ''}`;
        if (k < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.6 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [to, suffix]);
  return <span ref={ref}>0</span>;
}

function Magnetic({ children, href, primary, onClick }: {
  children: string; href: string; primary?: boolean; onClick?: () => void;
}) {
  const ref = useRef<HTMLAnchorElement | null>(null);
  const onMove = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
    const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
    el.style.transform = `translate(${dx * 6}px, ${dy * 5}px)`;
  };
  const onLeave = () => { if (ref.current) ref.current.style.transform = ''; };
  return (
    <a ref={ref} href={href} className={primary ? 'lp-btn lp-btn-gold' : 'lp-btn lp-btn-ghost'}
      onMouseMove={onMove} onMouseLeave={onLeave}
      onClick={onClick ? (e) => { e.preventDefault(); onClick(); } : undefined}>
      {children}
    </a>
  );
}

/* --------------------------------- page ---------------------------------- */

/**
 * The modules.
 *
 * ⚠ Each blurb names the thing the software REFUSES to do, not the thing it does. "Attendance" is a
 * checkbox every competitor has; "a timetable engine that refuses to double-book a teacher" is a
 * claim they'd have to build to match. The refusals are the product.
 */
const MODULES: { name: string; blurb: string; tag?: string }[] = [
  { name: 'Admissions', blurb: 'Enquiry to enrolled, with a funnel and a conversion rate. One admission can never produce two students.' },
  { name: 'Students', blurb: 'Guardians, classes, the roster everything else hangs off.' },
  { name: 'Attendance', tag: 'three ways', blurb: 'A name list, a grid of faces, or the QR on the ID card \u2014 the school switches on whichever it wants. Scanning only ever marks a child present; absence stays a person\u2019s decision. VTU\u2019s 85% eligibility engine is built in.' },
  { name: 'Fees & receipts', blurb: 'Concessions, installments, late-fee rules, defaulter report — and a branded receipt that prints.' },
  { name: 'Exams & results', blurb: 'Marks to SGPA. Results stay provisional until your grading rules are confirmed.' },
  { name: 'Timetable', blurb: 'A clash engine that refuses to double-book a teacher, a room, or a class.' },

  { name: 'Live bus tracking', tag: 'no hardware', blurb: 'The driver\u2019s phone is the tracker. Parents see the bus only while it is running — and never a child\u2019s location.' },
  { name: 'Pickup passes', tag: 'rotating code', blurb: 'The code changes every 30 seconds, so a screenshot is worthless. Every scan is logged — including the refused ones.' },
  { name: 'Hostel', blurb: 'An outpass stays open until a human signs the student back in. If they are late, the warden and the parent are both told.' },
  { name: 'Library', blurb: 'Copies, not titles \u2014 so \u201c2 of 3 available\u201d is true. Fines land on the fee account, not a cash tin.' },

  { name: 'The classroom', blurb: 'Homework with a due date and a live count of who has done it. Teachers write to parents \u2014 and the school can see the thread.' },
  { name: 'Communication', blurb: 'Announce to a class, a bus route, or everyone. It tells you who it will reach before you send it.' },

  { name: 'Staff records', blurb: 'Resumes, photos, bank, PAN, contracts. A promotion is recorded \u2014 never overwritten.' },
  { name: 'Recruitment', blurb: 'Applied to hired. Their resume comes with them when you hire them.' },
  { name: 'HR & leave', blurb: 'Balances only move on approval. An insufficient balance fails the approval, not the payslip.' },
  { name: 'Payroll', tag: 'CA-gated', blurb: 'Refuses to issue a payslip until your accountant verifies every statutory rate. It ships with none.' },

  { name: 'Report cards', tag: 'gated', blurb: 'Composed from marks already entered, never invented. It refuses to finalise while any subject is unmarked — and names them.' },
  { name: 'School store', blurb: 'Books and uniforms sized per student, plus parent re-orders that draw down real stock. Charges land on the fee account.' },
  { name: 'Houses & elections', blurb: 'A house leaderboard that is an auditable ledger, not a counter. Student elections take one vote per voter and refuse to break a tie.' },
  { name: 'Laboratories', blurb: 'Stock that cannot go negative, and breakages that bill through the same fee engine as everything else.' },

  { name: 'School trips', tag: 'consent-gated', blurb: 'A child travels only when a guardian has actively said yes. Silence is never permission, seats are capped, and the teacher carries the roster with emergency numbers on it.' },
  { name: 'Fee analytics', blurb: 'Collections month by month, how old the overdue money is, and which classes are lagging \u2014 every figure computed from the ledger, never estimated.' },
  { name: 'All campuses', tag: 'for trusts', blurb: 'One view across every campus and institution. Rates are weighted, not averaged \u2014 and results are never combined across boards, because a CBSE percentage and a VTU SGPA are different things.' },
  // ⚠ THE LEADERBOARD CLAIM BELONGS TO ONE CARD, NOT TWO. This card and “Houses & elections”
  // both made the same “auditable ledger, not a counter” argument in different words — on a page
  // whose entire pitch is precision, saying one thing twice reads as padding the list.
  { name: 'Sports & houses', blurb: 'Equipment issued and returned, meets and fixtures, and results that feed the house ledger rather than a separate scoreboard nobody reconciles.' },
  { name: 'Certificates', blurb: 'Bonafide, transfer, conduct \u2014 numbered, snapshotted, and reprintable years later exactly as issued.' },
  { name: 'Courier delivery', blurb: 'For the parcel that actually gets posted. Dispatched, delivered, or came back \u2014 with who signed for it.' },

  { name: 'Family portal', blurb: 'Attendance, fees, results, homework, the bus. Scoped to their own child, on every single request.' },
  { name: 'Install to phone', blurb: 'A real app on the home screen \u2014 no Play Store, no download, nothing to update.' },
];

/**
 * ⚠ These four used to say "coming soon". They have all SHIPPED — leaving them here was telling every
 * principal the product is LESS complete than it is, which is the most expensive kind of mistake a
 * landing page can make.
 *
 * What's genuinely next.
 */
// ⚠ Only genuinely UNBUILT things belong here. Listing shipped features as "coming soon" tells
// every principal the product is LESS complete than it is — the most expensive mistake a
// landing page can make. Fee analytics, multi-campus rollups and courier delivery all shipped.
// ⚠ FACE RECOGNITION BELONGS HERE, NOT IN THE MODULE LIST, and it will be tempting to move it.
// The consent register has shipped; the recognition has not. A principal who buys on the strength of
// a feature that does not exist is a principal who finds out in front of their parents — and this is
// the one feature where that failure is not a support ticket but a safeguarding row.
const NEXT_ORBIT = ['Face recognition attendance', 'Native mobile app', 'Board report-card formats', 'Alumni records'];

const FAQS: { q: string; a: string }[] = [
  {
    q: 'Do we need to buy GPS trackers for the buses?',
    a: 'No. The driver\u2019s phone is the tracker \u2014 the school starts the trip and sends them a link, with no app and no login. Parents see the bus only while it is running, only if their child is on that route, and never if their child is marked absent. Nothing is retained: when the bus arrives, the position is erased. We track the bus, never the child.',
  },
  {
    q: 'How do you stop a stranger collecting a child?',
    a: 'The pickup code changes every 30 seconds, so a screenshot is worthless within half a minute. The pass is tied to a real guardian link \u2014 not \u201cany parent\u201d, but that child\u2019s parent \u2014 and it is single-use. If a parent arrives without their phone, a guard cannot release the child alone: they raise a request, and the office rings the number already on file. Two people, in two places, and neither can act alone.',
  },
  {
    q: 'Can attendance be taken by face recognition?',
    // ⚠ THE HONEST ANSWER, AND IT SELLS BETTER THAN THE OTHER ONE. Every competitor will say yes.
    // A principal who has to answer to parents about their children's faces is not reassured by a
    // yes — they are reassured by a supplier who has clearly thought about the part that goes wrong.
    a: 'Not yet, and we would rather say so than sell you a demo. What is built today is the part that actually takes a term: the consent record. Every family answers for their own child \u2014 in the app or on a paper form the office keys in \u2014 and can withdraw at any time, in one tap, with no reason asked. The recognition itself is next. When it arrives, it will only ever be used for a child whose guardian has agreed; a school-level switch will never stand in for a parent\u2019s answer; and the ordinary register can never be switched off, because a flat battery must never mean a class cannot be marked.',
  },
  {
    q: 'Is our school\u2019s data separate from other schools?',
    a: 'Yes — every record carries your institution\u2019s identity and every query is filtered by it at the database layer, not in application code that can forget. One school can never see another\u2019s data.',
  },
  {
    q: 'Who decides payroll rates like PF and professional tax?',
    a: 'You and your accountant. Eduvora ships with zero statutory rates. Your CA enters and verifies each one, and payroll refuses to finalise while anything is unverified.',
  },
  {
    q: 'How do online fee payments work?',
    a: 'Through Razorpay. A payment is recorded only after cryptographic verification of the gateway\u2019s signature — a receipt can\u2019t exist for money that didn\u2019t arrive.',
  },
  {
    q: 'Can parents pay fees partially?',
    a: 'Your choice. Partial payment is a per-school setting; when it\u2019s off, the system itself refuses partial amounts.',
  },
  {
    q: 'We run an engineering college. Does Eduvora understand VTU?',
    a: 'Attendance eligibility (the 85% rule), per-subject grades and SGPA are built in — with every grade boundary editable, because your scheme is yours.',
  },
  {
    q: 'We live in Excel today. How do we move?',
    a: 'Start with one class and one fee structure — most schools are running attendance within a day. We help with the import.',
  },
];

/**
 * Numbers as words, because "28 modules" reads like a spec sheet and "Twenty-eight modules" reads
 * like a sentence. Outside the range it falls back to digits rather than being wrong.
 */
function spellCount(n: number): string {
  const words = [
    'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen',
    'Nineteen', 'Twenty',
  ];
  if (n <= 20) return words[n] ?? String(n);
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  if (n < 100) {
    const t = tens[Math.floor(n / 10)];
    const u = n % 10;
    return u === 0 ? t : `${t}-${words[u].toLowerCase()}`;
  }
  return String(n);
}

export function LandingPage() {
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  useReveal();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);

  return (
    <div className="lp" data-lp-theme={theme}>
      {/* ---------- nav ---------- */}
      <header className={`lp-nav ${scrolled ? 'lp-nav-solid' : ''}`}>
        <div className="lp-wrap lp-nav-inner">
          <a className="lp-logo" href="#top">
            <span className="lp-logo-orb" aria-hidden="true" />
            Eduvora
          </a>
          <nav className="lp-nav-links" aria-label="Sections">
            <a href="#modules">Modules</a>
            <a href="#gates">Trust</a>
            <a href="#showcase">Product</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </nav>
          <div className="lp-nav-cta">
            <button className="lp-theme" onClick={toggle} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}>
              {theme === 'dark' ? '☀' : '☾'}
            </button>
            <a className="lp-btn lp-btn-gold lp-btn-sm" href={DEMO_URL}>Open the demo</a>
          </div>
        </div>
      </header>

      {/* ---------- hero ---------- */}
      <section className="lp-hero" id="top">
        <OrbitCanvas mode={theme} />
        <div className="lp-wrap lp-hero-inner">
          <p className="lp-eyebrow" data-reveal>EDUVORA · SCHOOL OPERATING SYSTEM</p>
          <h1 className="lp-h1" data-reveal>
            The <em>whole</em> school.<br />One system.
          </h1>
          <p className="lp-sub" data-reveal>
            Admissions to attendance, fees to payroll — every part of the school in orbit around a
            single source of truth. Built for Indian schools and colleges.
          </p>
          <div className="lp-hero-cta" data-reveal>
            <Magnetic href={DEMO_URL} primary>Open the live demo</Magnetic>
            <Magnetic href="mailto:hello@eduvoratech.com?subject=Eduvora%20demo" onClick={() => openWhatsApp(WA_MESSAGES.demo)}>Book a walkthrough</Magnetic>
          </div>
          <p className="lp-trust" data-reveal>
            Multi-campus · VTU-aware · Razorpay fees · CA-gated payroll
          </p>
        </div>
        <div className="lp-scroll-hint" aria-hidden="true"><span /></div>
      </section>

      {/* ---------- modules ---------- */}
      <section className="lp-section" id="modules">
        <div className="lp-wrap">
          <div className="lp-divider" aria-hidden="true"><i /></div>
          <p className="lp-eyebrow" data-reveal>LIVE TODAY — NOT A ROADMAP</p>
          {/* ⚠ COUNTED, NOT TYPED. It said "Twenty-seven" and the list held twenty-eight. Nobody
              miscounted on purpose — somebody added a module, which is the one moment a hand-typed
              number is guaranteed to be missed. A public page that gets its own headline number
              wrong is the first thing a careful principal notices. */}
          <h2 className="lp-h2" data-reveal>{spellCount(MODULES.length)} modules.<br />One truth.</h2>
          <p className="lp-lead" data-reveal>
            Not twenty-seven tools stitched together — one system where a payment clears the defaulter
            report, an approved leave marks the register, a library fine lands on the fee account,
            and the bus a parent is watching is the one their child actually boarded.
          </p>
          <div className="lp-grid">
            {MODULES.map((m) => (
              <article className="lp-card" key={m.name} data-reveal>
                {/* ⚠ THE TAGS WERE WRITTEN AND NEVER RENDERED. Seven modules carried one — "no
                    hardware", "CA-gated", "consent-gated" — and none of them reached the page. They
                    are the shortest version of every argument on it, sitting in the source. */}
                <h3>{m.name}{m.tag && <span className="lp-card-tag">{m.tag}</span>}</h3>
                <p>{m.blurb}</p>
              </article>
            ))}
          </div>
          <div className="lp-status" data-reveal>
            <h3>Where the product actually stands</h3>
            <p>
              Every module listed above is built, compiled and running — not designed, not planned.
              Being straight about the edges matters more than a longer list:
            </p>
            <ul>
              <li><b>Live and in daily use.</b> Admissions, students, attendance, fees and receipts,
                exams, timetable, the parent portal — the core a school runs on.</li>
              <li><b>Built and ready, awaiting a school to use it in anger.</b> Hostel, library, trips,
                the store and courier delivery. Complete, but a first school will always find the
                rough edge that only real use reveals.</li>
              <li><b>Deliberately gated.</b> Payroll ships with <i>zero</i> statutory rates and refuses
                to issue a payslip until your accountant signs off each one. That is a feature, not an
                omission.</li>
              <li><b>Honest about boards.</b> CBSE, ICSE, State and PU grading are configurable per
                institution and never invented. Each board's own prescribed report-card format is
                still to come — the marks engine handles the maths, the paperwork template does not
                exist yet.</li>
            </ul>
          </div>

          <p className="lp-next" data-reveal>
            <span className="lp-next-label">NEXT IN ORBIT</span>
            {NEXT_ORBIT.map((n) => <span className="lp-next-chip" key={n}>{n}</span>)}
          </p>
        </div>
      </section>

      {/* ---------- gates ---------- */}
      <section className="lp-section lp-section-gates" id="gates">
        <div className="lp-wrap">
          <div className="lp-divider" aria-hidden="true"><i /></div>
          <p className="lp-eyebrow" data-reveal>WHY PRINCIPALS TRUST IT</p>
          <h2 className="lp-h2" data-reveal>
            Nothing important happens<br /><em>without a human.</em>
          </h2>
          <p className="lp-lead" data-reveal>
            Approval gates are enforced inside the system&rsquo;s core — not written in a policy
            document someone can skip.
          </p>
          <div className="lp-gates">
            <article className="lp-gate" data-reveal>
              <span className="lp-gate-no">GATE / FEES</span>
              <h3>Money is verified before it exists</h3>
              <p>
                An online payment is recorded only after the gateway&rsquo;s signature is
                cryptographically verified. A receipt cannot exist for money that didn&rsquo;t arrive.
              </p>
            </article>
            <article className="lp-gate" data-reveal>
              <span className="lp-gate-no">GATE / LEAVE</span>
              <h3>Balances move only on approval</h3>
              <p>
                A pending leave never consumes balance. Approval debits it in the same
                atomic step — insufficient balance makes the approval itself fail.
              </p>
            </article>
            <article className="lp-gate" data-reveal>
              <span className="lp-gate-no">GATE / PAYROLL</span>
              <h3>No payslip on an unverified number</h3>
              <p>
                Eduvora ships zero statutory rates. Your CA enters and verifies each one — and
                payroll refuses to finalise until every applied rule is signed off.
              </p>
            </article>

            <article className="lp-gate" data-reveal>
              <span className="lp-gate-no">GATE / THE HOSTEL</span>
              <h3>A student who leaves must come back</h3>
              <p>
                An outpass stays open until a human physically signs them back in. It never expires and
                never auto-closes. If they are late, the warden is alerted <em>and the parent is
                called</em> — because if the warden is asleep, nobody calls.
              </p>
            </article>

            <article className="lp-gate" data-reveal>
              <span className="lp-gate-no">GATE / THE GATE</span>
              <h3>A screenshot cannot collect a child</h3>
              <p>
                The pickup code changes every 30 seconds, so a photograph of it is worthless within
                half a minute. A guard can raise a request — but only the office can release a child,
                after ringing the number already on file.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* ---------- showcase ---------- */}
      <section className="lp-section" id="showcase">
        <div className="lp-wrap">
          <div className="lp-divider" aria-hidden="true"><i /></div>
          <p className="lp-eyebrow" data-reveal>THE MORNING VIEW</p>
          <h2 className="lp-h2" data-reveal>Walk in. Know everything.</h2>
          <div className="lp-stage" data-reveal>
            <div className="lp-frame" role="img" aria-label="Eduvora dashboard preview">
              <div className="lp-frame-bar"><i /><i /><i /><span>app.eduvoratech.com</span></div>
              <div className="lp-frame-body">
                <aside className="lp-frame-side">
                  <i className="on" /><i /><i /><i /><i /><i />
                </aside>
                <div className="lp-frame-main">
                  <div className="lp-kpis">
                    <div className="lp-kpi">
                      <span className="lp-kpi-label">Collections · this term</span>
                      <strong>₹42.6L</strong>
                      <div className="lp-bar"><i style={{ width: '72%' }} /></div>
                    </div>
                    <div className="lp-kpi">
                      <span className="lp-kpi-label">Attendance · today</span>
                      <div className="lp-ring" aria-hidden="true"><b>94%</b></div>
                    </div>
                    <div className="lp-kpi">
                      <span className="lp-kpi-label">Fee defaulters</span>
                      <strong>17</strong>
                      <span className="lp-chip-warn">follow-up due</span>
                    </div>
                  </div>
                  <div className="lp-rows">
                    <div className="lp-row"><span className="lp-dot ok" />Bus 3 departed · Jayanagar route<em>7:42 AM</em></div>
                    <div className="lp-row"><span className="lp-dot" />Leave approved · A. Rao (2 days)<em>8:05 AM</em></div>
                    <div className="lp-row"><span className="lp-dot ok" />Online payment verified · ₹52,000<em>8:12 AM</em></div>
                    <div className="lp-row"><span className="lp-dot warn" />Timetable clash refused · Room 204<em>8:20 AM</em></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="lp-phone" role="img" aria-label="Family portal on a phone">
              <div className="lp-phone-notch" />
              <p className="lp-phone-title">My family</p>
              <div className="lp-phone-card">
                <strong>Diya · 9-A</strong>
                <span>Attendance 96% · Fees ₹12,500 due</span>
                <button type="button" tabIndex={-1}>Pay online</button>
              </div>
              <div className="lp-phone-card lp-phone-card-2">
                <strong>Bus departed</strong>
                <span>Route A — Jayanagar · 7:42 AM</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- numbers ---------- */}
      <section className="lp-section lp-section-stats">
        <div className="lp-wrap lp-stats" data-reveal>
          <div><strong><Counter to={27} /></strong><span>modules live</span></div>
          <div><strong><Counter to={11} /></strong><span>gates that refuse bad data</span></div>
          <div><strong><Counter to={100} suffix="%" /></strong><span>tenant isolation, enforced in the database</span></div>
          <div><strong>1</strong><span>source of truth</span></div>
        </div>
      </section>

      {/* ---------- personas ---------- */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-divider" aria-hidden="true"><i /></div>
          <h2 className="lp-h2" data-reveal>Everyone sees their school.<br />Only theirs.</h2>
          <div className="lp-personas">
            <article data-reveal>
              <h3>Management</h3>
              <p>Collections, attendance, defaulters and staff — live, on one screen. Support access is read-only, time-boxed, and visible to you.</p>
            </article>
            <article data-reveal>
              <h3>Teachers</h3>
              <p>Mark a class in seconds. Apply for leave from your phone. Never get double-booked — the timetable refuses the clash before it happens.</p>
            </article>
            <article data-reveal>
              <h3>Families</h3>
              <p>A parent sees their child — attendance, fees, results — and pays online. Scoped to their children and no one else&rsquo;s, structurally.</p>
            </article>
          </div>
        </div>
      </section>

      {/* ---------- pricing ---------- */}
      <section className="lp-section" id="pricing">
        <div className="lp-wrap">
          <div className="lp-divider" aria-hidden="true"><i /></div>
          <p className="lp-eyebrow" data-reveal>PRICING</p>
          <h2 className="lp-h2" data-reveal>Scales with enrolment,<br />not with modules.</h2>
          <p className="lp-lead" data-reveal>Every plan includes every module. No per-feature upsells.</p>
          <div className="lp-plans">
            <article className="lp-plan" data-reveal>
              <h3>Starter</h3>
              <p className="lp-plan-for">Single school, up to 500 students</p>
              <ul>
                <li>All 27 modules</li>
                <li>Family portal + online fees</li>
                <li>Email support</li>
              </ul>
              <button type="button" className="lp-btn lp-btn-ghost" onClick={() => openWhatsApp(WA_MESSAGES.starter)}>Talk to us</button>
            </article>
            <article className="lp-plan lp-plan-hi" data-reveal>
              <h3>Growth</h3>
              <p className="lp-plan-for">Schools &amp; colleges up to 3,000 students</p>
              <ul>
                <li>Everything in Starter</li>
                <li>Transport alerts + HR &amp; payroll</li>
                <li>Priority support, onboarding help</li>
              </ul>
              <button type="button" className="lp-btn lp-btn-gold" onClick={() => openWhatsApp(WA_MESSAGES.growth)}>Talk to us</button>
            </article>
            <article className="lp-plan" data-reveal>
              <h3>Trust</h3>
              <p className="lp-plan-for">Multi-campus trusts &amp; groups</p>
              <ul>
                <li>Everything in Growth</li>
                <li>Multiple campuses, one console</li>
                <li>Dedicated onboarding</li>
              </ul>
              <button type="button" className="lp-btn lp-btn-ghost" onClick={() => openWhatsApp(WA_MESSAGES.trust)}>Talk to us</button>
            </article>
          </div>
        </div>
      </section>

      {/* ---------- faq ---------- */}
      <section className="lp-section" id="faq">
        <div className="lp-wrap lp-faq-wrap">
          <div className="lp-divider" aria-hidden="true"><i /></div>
          <h2 className="lp-h2" data-reveal>Fair questions.</h2>
          <div className="lp-faq">
            {FAQS.map((f) => (
              <details key={f.q} data-reveal>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- final cta ---------- */}
      <section className="lp-final">
        <div className="lp-wrap" data-reveal>
          <h2 className="lp-h2">See your school in orbit.</h2>
          <p className="lp-lead">The demo is live, seeded with a full school — open it now.</p>
          <div className="lp-hero-cta">
            <Magnetic href={DEMO_URL} primary>Open the live demo</Magnetic>
            <Magnetic href="mailto:hello@eduvoratech.com?subject=Eduvora%20walkthrough" onClick={() => openWhatsApp(WA_MESSAGES.demo)}>Book a walkthrough</Magnetic>
          </div>
        </div>
      </section>

      {/* ---------- footer ---------- */}
      <footer className="lp-footer">
        <div className="lp-wrap lp-footer-inner">
          <div>
            <a className="lp-logo" href="#top"><span className="lp-logo-orb" aria-hidden="true" />Eduvora</a>
            {/* ⚠ THIS BLOCK IS A VERIFICATION DOCUMENT, NOT DECORATION. Meta, payment gateways and
                DLT operators all open the website listed on the application and check that the legal
                name, the registered address and the phone number match the papers. It said
                "Bengaluru" — the company is registered at Davanagere, so the one place a reviewer
                looks contradicted the Udyam certificate. Anything changed here has to keep matching
                UDYAM-KR-12-0072483. */}
            <p>The school operating system.</p>
            <address className="lp-legal">
              A product of <strong>AIVENTRIX TECHNOLOGIES</strong><br />
              3619/6 &amp; 3619/7, Matru Krupa, 4th Main, 13th Cross,<br />
              SS Layout, Davanagere, Karnataka 577006, India<br />
              <a href="tel:+918884046487">+91 88840 46487</a> · <a href="mailto:hello@eduvoratech.com">hello@eduvoratech.com</a><br />
              Udyam Reg. UDYAM-KR-12-0072483
            </address>
          </div>
          <nav aria-label="Footer">
            <a href="#modules">Modules</a>
            <a href="#gates">Trust</a>
            <a href="#pricing">Pricing</a>
            <a href="/terms">Terms</a>
            <a href="/privacy">Privacy</a>
            <a href="/delete-account">Delete account</a>
            <a href="#" onClick={(e) => { e.preventDefault(); openWhatsApp(WA_MESSAGES.support); }}>WhatsApp us</a>
            <a href="mailto:hello@eduvoratech.com">hello@eduvoratech.com</a>
          </nav>
        </div>
        <p className="lp-fine">© {new Date().getFullYear()} AIVENTRIX TECHNOLOGIES. Made in Karnataka.</p>
      </footer>

      {/* Floating WhatsApp. Deliberately last in the DOM and revealed on scroll — a chat bubble over
          the hero before anyone has read a word is an irritation, not an invitation. */}
      <WhatsAppFab />
    </div>
  );
}
